/* eslint-disable jsx-a11y/no-autofocus */
import React, { FunctionComponent, useState, useEffect, ChangeEvent } from 'react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import InputMask from 'react-input-mask';
import { is, replace } from 'ramda';
import PhoneInput from 'react-phone-input-2';
import { IBindingAction, IBindingCallback1 } from '../../common/models/callback';
import { IFormattedMessage } from '../../common/models/common/IFormattedMessage';
import { concat } from '../../common/helpers/arrayHelper';
import Icon from '../Icon';
import { isMobileDevice } from '../../common/helpers/deviceHelper';
import styles from './styles.module.scss';
import 'react-phone-input-2/lib/style.css';

interface IProps {
  intl: IntlShape;
  className?: string;
  size?: 'sm' | 'lg';
  value?: string;
  autoFocus?: boolean;
  variant?: 'boxed' | 'underlined';
  label?: IFormattedMessage;
  error?: boolean;
  success?: boolean;
  errorIntl?: IFormattedMessage | string;
  type?: 'text' | 'email' | 'phoneNumber' | 'password' | 'date';
  placeholder?: IFormattedMessage;
  disableAutocomplete?: boolean;
  disabled?: boolean;
  dropdownPosition?: 'top' | 'bottom';
  disableDropdown?: boolean;
  onChange?: IBindingCallback1<string>;
  onBlur?: IBindingAction;
  onClick?: IBindingAction | IBindingCallback1<React.MouseEvent<HTMLInputElement | string, MouseEvent>>;
  onFocus?: IBindingAction;
}

const Input: FunctionComponent<IProps> = ({
  className = '',
  size = 'sm',
  placeholder,
  value: input,
  autoFocus,
  variant = 'boxed',
  errorIntl,
  success,
  label,
  error,
  intl,
  type = 'text',
  disableAutocomplete,
  disabled,
  dropdownPosition = 'bottom',
  disableDropdown = false,
  onChange = () => null,
  onBlur,
  onClick,
  onFocus
}) => {
  const val = input || '';

  const [value, setValue] = useState(val);
  const iconShown = Boolean(errorIntl) || error || success;
  const classes = concat(styles[variant],
    iconShown && styles.iconInput,
    Boolean(errorIntl || error) && styles.errorInput);
  const placeholderText = placeholder && intl.formatMessage(placeholder);

  useEffect(() => {
    if (value !== val) {
      setValue(val);
    }
  }, [val]);

  const change = (v: string) => {
    setValue(v);
    if (type === 'date') {
      onChange(replace(/\D/g, '', v));
    } else {
      onChange(v);
    }
  };

  const getInput = () => {
    const commonProps = {
      className: classes,
      onChange: ({ currentTarget }: ChangeEvent<HTMLInputElement>) => change(currentTarget.value),
      autoComplete: disableAutocomplete ? 'disabled' : undefined,
      autoFocus: isMobileDevice() ? undefined : autoFocus,
      disabled,
      value,
      onBlur,
      onClick,
      onFocus
    };

    switch (type) {
      case 'phoneNumber':
        return (
          <PhoneInput
            country="us"
            value={value}
            onChange={phone => change(phone && `+${phone}`)}
            containerClass={styles.phoneNumberInputContainer}
            inputProps={{
              className: `${classes} ${styles.phoneNumberInput}`,
              autoFocus: isMobileDevice() ? undefined : autoFocus,
              autoComplete: disableAutocomplete ? 'disabled' : undefined
            }}
            dropdownClass={styles[`dropdown-${dropdownPosition}`]}
            onBlur={onBlur}
            onClick={onClick}
            onFocus={onFocus}
            disabled={disabled}
            disableDropdown={disableDropdown}
            placeholder={placeholderText || ''}
            enableSearch
            disableSearchIcon
          />
        );
      case 'date':
        return (
          <InputMask
            {...commonProps}
            mask="99 / 9999"
            placeholder="mm / yyyy"
            type="tel"
          />
        );
      default:
        return (
          <input
            {...commonProps}
            type={type}
            placeholder={placeholderText}
          />
        );
    }
  };

  return (
    <div className={`position-relative ${styles.inputContainer} ${styles[size]} ${className}`}>
      {label && <div className={styles.label}><FormattedMessage {...label} /></div>}
      {getInput()}
      {(errorIntl || error) && (
        <>
          <Icon icon="times" className={`${styles.icon} ${styles.errorIcon}`} />
          {errorIntl && (
            <div className={styles.errorMsg}>
              {is(String, errorIntl)
                ? <FormattedMessage id={errorIntl as string} defaultMessage={errorIntl as string} />
                : <FormattedMessage {...(errorIntl as IFormattedMessage)} />}
            </div>
          )}
        </>
      )}
      {success && <Icon icon="check" className={`${styles.icon} ${styles.successIcon}`} />}
    </div>
  );
};

export default injectIntl(Input);
